import React, { Component } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import MuiLink from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import {
  NodeAgentPicker,
  NodeContainer,
  NodeSection,
  NodeTextField,
  NODE_UI,
} from "../../NodeUI";
import AgentAvatar from "../AgentAvatar";
import ToggleRow from "../../common/ToggleRow";
import PlanTemplateEditor from "./PlanTemplateEditor";
import { updateManagedRunPolicy } from "./managedRunApi";
import {
  DEFAULT_OPERATOR_AGENT_VALUE,
  OPERATOR_TOOLS,
  REQUIRED_OPERATOR_TOOLS,
  clonePolicy,
  defaultPolicy,
  emptyStep,
  normalizeEnabledTools,
  normalizePolicy,
  serializePolicy,
} from "./planTemplateModel";
import { loadDraftProject } from "../../../utils/canvasApi";
import { send_request } from "../../../utils/Request";
import { withParams } from "../../../Routes";

const PageRoot = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  padding: theme.spacing(1.5, 2, 2),
  backgroundColor: theme.palette.background.paper,
}));

const Header = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.caption,
  lineHeight: 1.4,
  marginTop: NODE_UI.spacing.xs,
}));

const IconBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  width: 36,
  height: 36,
  borderRadius: NODE_UI.borderRadiusSm,
  flexShrink: 0,
  display: "grid",
  placeItems: "center",
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.14)
    : alpha(theme.palette.text.secondary, 0.08),
  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
}));

const OperatorToolGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: NODE_UI.spacing.md,
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
}));

const OperatorToolCard = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "interactive" && prop !== "required",
})(({ theme, selected, interactive, required }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: NODE_UI.spacing.md,
  padding: NODE_UI.spacing.md,
  border: "1px solid",
  borderColor: selected
    ? alpha(theme.palette.primary.main, 0.55)
    : theme.palette.divider,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07)
    : theme.palette.mode === "dark"
    ? "rgba(255,255,255,0.02)"
    : "#fafafa",
  cursor: interactive ? "pointer" : "default",
  opacity: selected || required ? 1 : 0.58,
  transition: "background-color 0.12s, border-color 0.12s, opacity 0.12s",
  "&:hover": interactive ? { borderColor: theme.palette.primary.main, opacity: 1 } : undefined,
}));

const ToolTitle = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.35,
}));

const ToolDescription = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginTop: 2,
}));

const ToolName = styled(Typography)(({ theme }) => ({
  display: "inline-block",
  marginTop: NODE_UI.spacing.xs,
  fontFamily: "monospace",
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
}));

const ActionRow = styled(Stack)(({ theme }) => ({
  justifyContent: "flex-end",
  flexDirection: "row",
  gap: theme.spacing(1),
  paddingTop: NODE_UI.spacing.md,
}));

class ManagedRunPolicy extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      workflowName: "",
      agents: [],
      policy: defaultPolicy(),
      hasChanges: false,
      snackbar: { open: false, message: "", severity: "success" },
    };
  }

  componentDidMount() {
    this.fetchProjectData();
  }

  fetchProjectData = async () => {
    const { id } = this.props.params;
    this.setState({ loading: true });
    try {
      const [projectResult, agentsResult] = await Promise.allSettled([
        loadDraftProject(id),
        send_request("project-service/agent/list", "", {}, "GET"),
      ]);
      if (projectResult.status === "rejected") {
        throw projectResult.reason;
      }
      const project = projectResult.value?.data;
      this.setState({
        workflowName: project?.name || "Untitled",
        agents:
          agentsResult.status === "fulfilled" ? agentsResult.value?.data || [] : [],
        policy: normalizePolicy(project?.extras?.managedRunPolicy),
        loading: false,
        hasChanges: false,
      });
    } catch (error) {
      console.error("Error fetching managed-run policy:", error);
      this.setState({ loading: false });
      this.showSnackbar("Failed to load managed run policy", "error");
    }
  };

  updatePolicy = (updater) => {
    this.setState((prev) => {
      const nextPolicy = clonePolicy(prev.policy);
      updater(nextPolicy);
      return { policy: nextPolicy, hasChanges: true };
    });
  };

  handleStepChange = (index, nextStep) => {
    this.updatePolicy((next) => {
      next.planTemplate.tasks[index] = nextStep;
    });
  };

  handleStepRemove = (index) => {
    this.updatePolicy((next) => {
      next.planTemplate.tasks.splice(index, 1);
    });
  };

  handleAddStep = () => {
    this.updatePolicy((next) => {
      next.planTemplate.tasks.push(emptyStep());
    });
  };

  handleSave = async () => {
    const { id } = this.props.params;
    this.setState({ saving: true });
    try {
      await updateManagedRunPolicy(id, serializePolicy(this.state.policy));
      this.setState({ saving: false, hasChanges: false });
      this.showSnackbar("Managed run policy saved", "success");
    } catch (error) {
      console.error("Error saving managed-run policy:", error);
      this.setState({ saving: false });
      this.showSnackbar("Failed to save managed run policy", "error");
    }
  };

  handleCancel = () => {
    if (this.props.isModal && this.props.onClose) {
      this.props.onClose();
    } else {
      this.fetchProjectData();
    }
  };

  getSelectedExistingAgent(policy = this.state.policy) {
    if (!policy?.operatorAgentId) return null;
    return (this.state.agents || []).find(
      (agent) => agent.agentId === policy.operatorAgentId
    );
  }

  getOperatorAgentOptions() {
    return [
      {
        agentId: DEFAULT_OPERATOR_AGENT_VALUE,
        name: "Workflow86 managed operator",
        description:
          "Create and maintain a workflow-specific operator agent automatically.",
        defaultOperator: true,
      },
      ...(this.state.agents || []).map((agent) => ({
        ...agent,
        defaultOperator: false,
      })),
    ];
  }

  getSelectedOperatorOption(policy = this.state.policy) {
    if (!policy?.operatorAgentId) {
      return this.getOperatorAgentOptions()[0];
    }
    return (
      this.getOperatorAgentOptions().find(
        (option) => option.agentId === policy.operatorAgentId
      ) || {
        agentId: policy.operatorAgentId,
        name: "Unavailable agent",
        description: policy.operatorAgentId,
        missing: true,
      }
    );
  }

  showSnackbar = (message, severity) => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  handleCloseSnackbar = () => {
    this.setState((prev) => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  toggleOperatorTool(toolName) {
    if (REQUIRED_OPERATOR_TOOLS.includes(toolName)) return;
    this.updatePolicy((next) => {
      const current = new Set(normalizeEnabledTools(next.enabledTools));
      if (current.has(toolName)) current.delete(toolName);
      else current.add(toolName);
      next.enabledTools = normalizeEnabledTools(Array.from(current));
    });
  }

  renderAgentOption(props, option) {
    const isDefault = option?.defaultOperator;
    return (
      <Box component="li" {...props} sx={{ display: "flex !important", alignItems: "center", gap: 1.25, py: 1 }}>
        <AgentAvatar agent={option} name={option?.name || "Agent"} size={30} borderRadius="8px" />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }} noWrap>
            {option?.name || option?.agentId || "Untitled agent"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3 }} noWrap>
            {option?.description ||
              (isDefault ? "Workflow-specific managed operator" : option?.agentId || "")}
          </Typography>
        </Box>
      </Box>
    );
  }

  renderOperatorTool(tool, enabledTools, readOnly) {
    const selected = enabledTools.includes(tool.toolName);
    const interactive = !tool.required && !readOnly;
    const handleToggle = () => this.toggleOperatorTool(tool.toolName);
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        handleToggle();
      }
    };
    return (
      <OperatorToolCard
        key={tool.toolName}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        selected={selected}
        interactive={interactive}
        required={tool.required}
        aria-pressed={interactive ? selected : undefined}
        onClick={interactive ? handleToggle : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
      >
        <IconBadge selected={selected}>
          <BuildOutlinedIcon fontSize="small" />
        </IconBadge>
        <Box sx={{ minWidth: 0 }}>
          <ToolTitle>{tool.title}</ToolTitle>
          <ToolDescription>{tool.description}</ToolDescription>
          <ToolName>{tool.toolName}</ToolName>
          {readOnly ? (
            <ToolDescription>
              {selected ? "Enabled on selected agent" : "Not enabled on selected agent"}
            </ToolDescription>
          ) : tool.required ? (
            <ToolDescription>Always enabled</ToolDescription>
          ) : null}
        </Box>
      </OperatorToolCard>
    );
  }

  render() {
    const { id } = this.props.params;
    const { isModal } = this.props;
    const { loading, saving, workflowName, policy, hasChanges, snackbar } = this.state;
    const selectedExistingAgent = this.getSelectedExistingAgent(policy);
    const usesExistingAgent = Boolean(policy.operatorAgentId);
    const selectedAgentMissing = usesExistingAgent && !selectedExistingAgent;
    const displayedEnabledTools = usesExistingAgent
      ? selectedExistingAgent?.enabledTools || []
      : normalizeEnabledTools(policy.enabledTools);
    const missingRequiredTools = usesExistingAgent
      ? REQUIRED_OPERATOR_TOOLS.filter((toolName) => !displayedEnabledTools.includes(toolName))
      : [];
    const steps = policy.planTemplate?.tasks || [];

    return (
      <PageRoot sx={isModal ? { pt: 1 } : undefined}>
        {!isModal && (
          <Breadcrumbs separator=">" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} underline="hover" to="/project">
              Workflows
            </MuiLink>
            <MuiLink component={RouterLink} underline="hover" to={`/project/canvas/${id}`}>
              {workflowName || "Workflow"}
            </MuiLink>
            <Typography color="text.primary">Managed run</Typography>
          </Breadcrumbs>
        )}

        {!isModal && (
          <Header>
            <IconButton size="small" onClick={() => this.props.navigate(`/project/canvas/${id}`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
              Managed run for {workflowName || "Workflow"}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={this.fetchProjectData}
                disabled={loading}
                sx={{ color: "text.secondary" }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Header>
        )}

        {loading ? (
          <NodeContainer sx={{ padding: 0 }}>
            <Skeleton variant="text" width={220} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1, mt: 2 }} />
          </NodeContainer>
        ) : (
          <NodeContainer sx={{ padding: 0, gap: NODE_UI.spacing.lg }}>
            <NodeSection title="Mode">
              <ToggleRow
                checked={policy.enabled}
                onChange={(enabled) =>
                  this.updatePolicy((next) => {
                    next.enabled = enabled;
                  })
                }
                title="Enable managed run"
                description="New production sessions for this workflow will receive an operator chat seeded with the plan below."
              />
            </NodeSection>

            {policy.enabled ? (
              <>
                <NodeSection title="Operator agent">
              <NodeAgentPicker
                label="Agent"
                value={this.getSelectedOperatorOption(policy)}
                agents={this.getOperatorAgentOptions()}
                loading={loading}
                placeholder="Search agents..."
                noOptionsText="No agents found"
                inDialog={isModal}
                showCard={Boolean(selectedExistingAgent)}
                renderOption={(props, option) => this.renderAgentOption(props, option)}
                getOptionLabel={(option) => option?.name || option?.agentId || "Untitled agent"}
                onChange={(nextOption) =>
                  this.updatePolicy((next) => {
                    const nextAgentId = nextOption?.agentId;
                    next.operatorAgentId =
                      !nextAgentId || nextAgentId === DEFAULT_OPERATOR_AGENT_VALUE
                        ? null
                        : nextAgentId;
                  })
                }
              />
              <HelperText>
                Use the Workflow86 managed operator to create and maintain a workflow-specific agent
                automatically, or select an existing agent to run this plan.
              </HelperText>
              {selectedAgentMissing ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The selected agent is not available. Choose another agent or use the Workflow86
                  managed operator.
                </Alert>
              ) : null}
              {missingRequiredTools.length > 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The selected agent is missing required managed run tools:{" "}
                  {missingRequiredTools.join(", ")}.
                </Alert>
              ) : null}
            </NodeSection>

            <NodeSection title="Instructions">
              <NodeTextField
                label="Operator guidance"
                fullWidth
                multiline
                minRows={3}
                value={policy.instructions || ""}
                onChange={(nextValue) =>
                  this.updatePolicy((next) => {
                    next.instructions = nextValue;
                  })
                }
                helperText="Optional standing guidance layered onto the operator agent's system prompt."
              />
            </NodeSection>

            <NodeSection title="Goal & plan">
              <PlanTemplateEditor
                goal={policy.planTemplate?.goal || ""}
                tasks={steps}
                onGoalChange={(nextValue) =>
                  this.updatePolicy((next) => {
                    next.planTemplate.goal = nextValue;
                  })
                }
                onStepChange={this.handleStepChange}
                onStepRemove={this.handleStepRemove}
                onAddStep={this.handleAddStep}
                goalHelperText="The outcome the operator is driving this run toward."
                stepsHelperText="The operator's task queue is seeded with these steps. Time-bound steps get scheduled wakes: a recurring check and a deadline that trigger the if-met / if-not-met actions."
              />
            </NodeSection>

            <NodeSection title="Tools the operator can use">
              <OperatorToolGrid>
                {OPERATOR_TOOLS.map((tool) =>
                  this.renderOperatorTool(tool, displayedEnabledTools, usesExistingAgent)
                )}
              </OperatorToolGrid>
              <HelperText>
                {usesExistingAgent
                  ? "These reflect the selected agent's current tools. Edit that agent to change them."
                  : "These tools are written to the workflow-specific operator agent."}
              </HelperText>
            </NodeSection>
              </>
            ) : null}

            <ActionRow>
              <Button variant="outlined" color="inherit" onClick={this.handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? "Saving..." : "Save policy"}
              </Button>
            </ActionRow>
          </NodeContainer>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageRoot>
    );
  }
}

export default withParams(ManagedRunPolicy);
